import { useEffect, useState, type CSSProperties } from 'react';
import type { Customer } from '@setapart/shared';
import { FONTS, COLORS } from '@setapart/shared';
import { TrendingUp, CreditCard, Copy, Check, AlertCircle } from 'lucide-react';
import { fetchClicks, fetchPayouts, fetchSummary, type CreatorSummary } from '../lib/api';

interface Props {
  customer: Customer;
  onConnectStripe: () => void;
}

export function Dashboard({ customer, onConnectStripe }: Props) {
  const [summary, setSummary] = useState<CreatorSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clickStats, setClickStats] = useState<Array<{ day: string; brand: string | null; n: number }>>([]);
  const [payouts, setPayouts] = useState<Array<Record<string, unknown>>>([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await fetchSummary();
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setSummary(res);
      const [clicks, ps] = await Promise.all([fetchClicks(30), fetchPayouts()]);
      if (clicks.ok && clicks.clicks) setClickStats(clicks.clicks);
      if (ps.ok && ps.payouts) setPayouts(ps.payouts);
    })();
  }, []);

  if (error === 'not_a_creator') {
    return (
      <div>
        <div style={{ fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>
          NOT PROVISIONED
        </div>
        <h1 style={{ fontFamily: FONTS.display, fontSize: 28, color: COLORS.ink, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>
          Hi {customer.email} — you're signed in, but not yet provisioned as a creator.
        </h1>
        <p style={{ fontSize: 14, color: COLORS.muted, marginTop: 8, maxWidth: 520, lineHeight: 1.5 }}>
          Reach out to <a href="mailto:hi@setapart.app" style={{ color: COLORS.accent }}>hi@setapart.app</a>{' '}
          and we'll get your code set up. You'll sign in with this same email.
        </p>
      </div>
    );
  }

  if (!summary) return <div style={{ color: COLORS.muted }}>Loading your dashboard…</div>;

  const connected = summary.stripe.chargesEnabled;
  const refLink = `https://setapart.app/?ref=${summary.creator.code}`;
  const shortLink = `https://setapart.app/ref/${summary.creator.code}`;

  async function copyRef() {
    try {
      await navigator.clipboard.writeText(shortLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  }

  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.4em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.accent, marginBottom: 8 }}>
        DASHBOARD
      </div>
      <h1 style={{ fontFamily: FONTS.display, fontSize: 32, color: COLORS.ink, margin: 0, fontStyle: 'italic', fontWeight: 500 }}>
        {summary.creator.name}
      </h1>
      <div style={{ fontSize: 14, color: COLORS.muted, marginTop: 6 }}>@{summary.creator.handle} · {Math.round(summary.creator.commissionRate * 100)}% of platform fee</div>

      {/* Code + share link */}
      <section style={cardPrimary}>
        <div style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.muted, marginBottom: 6 }}>
          YOUR CODE
        </div>
        <div style={{ fontFamily: FONTS.display, fontSize: 40, color: COLORS.ink, fontWeight: 500, letterSpacing: '-0.02em' }}>
          {summary.creator.code}
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.muted, marginBottom: 6 }}>
            SHARE LINK
          </div>
          <div style={linkRow}>
            <code style={{ fontSize: 13, color: COLORS.ink, wordBreak: 'break-all' }}>{shortLink}</code>
            <button onClick={copyRef} style={copyBtn}>
              {copied ? <Check size={14} color={COLORS.ink} /> : <Copy size={14} color={COLORS.ink} />}
            </button>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <section style={kpiRow}>
        <Kpi label="YTD Earned" cents={summary.ytd.grossCents} />
        <Kpi label="Paid Out" cents={summary.ytd.paidCents} />
        <Kpi label="Pending" cents={summary.ytd.pendingCents} tone="accent" />
        <Kpi label="Clawbacks YTD" cents={summary.ytd.clawbackCents} tone="muted" />
      </section>

      {/* Connect Stripe */}
      <section style={cardNeutral}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <CreditCard size={20} color={COLORS.ink} />
          <div style={{ fontFamily: FONTS.display, fontSize: 18, color: COLORS.ink, fontWeight: 500, fontStyle: 'italic' }}>
            Payouts via Stripe
          </div>
        </div>
        {connected ? (
          <>
            <div style={{ color: '#1C3A2E', fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              CONNECTED — {summary.stripe.status}
            </div>
            <p style={{ fontSize: 12, color: COLORS.muted, marginTop: 6 }}>
              Account <code>{summary.stripe.accountId}</code>. Daily payouts from Stripe.
            </p>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: COLORS.ink, maxWidth: 560, lineHeight: 1.5 }}>
              Connect Stripe to receive your monthly payouts. Takes about 5 minutes.
              Until then, earnings accrue in your pending balance and will transfer on your first connected month.
            </p>
            <button onClick={onConnectStripe} style={primaryBtn}>
              Connect Stripe →
            </button>
          </>
        )}
      </section>

      {summary.ytd.pendingClawbackCents > 0 && (
        <section style={alertBox} data-testid="clawback-alert">
          <AlertCircle size={16} color={COLORS.accent} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent }}>Pending clawbacks: ${(summary.ytd.pendingClawbackCents / 100).toFixed(2)}</div>
            <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 4 }}>
              Refunds on prior sales create clawbacks that are netted out of your next monthly payout. Nothing to do on your end.
            </div>
          </div>
        </section>
      )}

      {/* Recent clicks */}
      <section style={cardNeutral}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <TrendingUp size={20} color={COLORS.ink} />
          <div style={{ fontFamily: FONTS.display, fontSize: 18, color: COLORS.ink, fontWeight: 500, fontStyle: 'italic' }}>
            Last 30 days
          </div>
        </div>
        {clickStats.length === 0 ? (
          <div style={{ fontSize: 13, color: COLORS.muted }}>No clicks yet. Share your link above.</div>
        ) : (
          <div style={tableStyle}>
            <div style={{ ...tableHeader, gridTemplateColumns: '1fr 1fr 80px' }}>
              <span>Day</span>
              <span>Brand</span>
              <span style={{ textAlign: 'right' }}>Clicks</span>
            </div>
            {clickStats.slice(0, 10).map((row, i) => (
              <div key={i} style={{ ...tableRow, gridTemplateColumns: '1fr 1fr 80px' }}>
                <span>{row.day}</span>
                <span>{row.brand ?? '—'}</span>
                <span style={{ textAlign: 'right' }}>{row.n}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Payout history */}
      <section style={cardNeutral}>
        <div style={{ fontFamily: FONTS.display, fontSize: 18, color: COLORS.ink, fontWeight: 500, fontStyle: 'italic', marginBottom: 12 }}>
          Payout history
        </div>
        {payouts.length === 0 ? (
          <div style={{ fontSize: 13, color: COLORS.muted }}>No payouts yet. First payout runs on the 1st of next month.</div>
        ) : (
          <div style={tableStyle}>
            <div style={{ ...tableHeader, gridTemplateColumns: '1fr 1fr 1fr 100px' }}>
              <span>Period</span>
              <span>Gross</span>
              <span>Clawback</span>
              <span style={{ textAlign: 'right' }}>Net</span>
            </div>
            {payouts.map((p, i) => {
              const periodStart = p['period_start'] as number;
              const gross = (p['gross_attributed_cents'] as number) ?? 0;
              const clawback = (p['clawback_cents'] as number) ?? 0;
              const net = (p['net_cents'] as number) ?? 0;
              const status = p['status'] as string;
              return (
                <div key={i} style={{ ...tableRow, gridTemplateColumns: '1fr 1fr 1fr 100px' }}>
                  <span>
                    {new Date(periodStart).toISOString().slice(0, 7)}{' '}
                    <span style={{ fontSize: 10, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.1em', marginLeft: 6 }}>
                      {status}
                    </span>
                  </span>
                  <span>${(gross / 100).toFixed(2)}</span>
                  <span>{clawback > 0 ? `-$${(clawback / 100).toFixed(2)}` : '—'}</span>
                  <span style={{ textAlign: 'right', fontWeight: 700 }}>${(net / 100).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, cents, tone = 'ink' }: { label: string; cents: number; tone?: 'ink' | 'accent' | 'muted' }) {
  const color = tone === 'accent' ? COLORS.accent : tone === 'muted' ? COLORS.muted : COLORS.ink;
  return (
    <div style={kpiStyle}>
      <div style={{ fontSize: 9, letterSpacing: '0.3em', textTransform: 'uppercase', fontWeight: 700, color: COLORS.muted }}>
        {label}
      </div>
      <div style={{ fontFamily: FONTS.display, fontSize: 28, color, fontWeight: 500, marginTop: 6 }}>
        ${(cents / 100).toFixed(2)}
      </div>
    </div>
  );
}

const cardPrimary: CSSProperties = {
  marginTop: 24,
  padding: 20,
  borderRadius: 16,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
  maxWidth: 560,
};
const cardNeutral: CSSProperties = {
  marginTop: 16,
  padding: 20,
  borderRadius: 16,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
};
const kpiRow: CSSProperties = {
  marginTop: 16,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
  gap: 10,
};
const kpiStyle: CSSProperties = {
  padding: 14,
  borderRadius: 14,
  background: '#fff',
  border: `1px solid ${COLORS.sand}`,
};
const linkRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  background: COLORS.cream,
  borderRadius: 10,
  border: `1px solid ${COLORS.sand}`,
};
const copyBtn: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${COLORS.sand}`,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
const alertBox: CSSProperties = {
  marginTop: 12,
  padding: 12,
  background: 'rgba(184,71,46,0.08)',
  border: `1px solid rgba(184,71,46,0.3)`,
  borderRadius: 12,
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
};
const primaryBtn: CSSProperties = {
  marginTop: 12,
  padding: '12px 20px',
  borderRadius: 999,
  background: COLORS.ink,
  color: COLORS.cream,
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  fontWeight: 700,
};
const tableStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 13,
  color: COLORS.ink,
};
const tableHeader: CSSProperties = {
  display: 'grid',
  padding: '6px 0',
  fontSize: 10,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  fontWeight: 700,
  color: COLORS.muted,
  borderBottom: `1px solid ${COLORS.sand}`,
};
const tableRow: CSSProperties = {
  display: 'grid',
  padding: '8px 0',
  borderBottom: `1px solid rgba(217,205,184,0.5)`,
};
